import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  addDoc,
  query,
  orderBy,
  where,
  Timestamp,
  writeBatch,
} from 'firebase/firestore';
import { db, auth } from './config';
import type { ActivityDefinition, ActivityLog } from '../../types/activityTracking';
import { SUGGESTED_ACTIVITIES } from '../../content/activityTemplates';

// ─── Activity Definitions ────────────────────────────────────────────────────

export async function getActivityDefinitions(userId: string): Promise<ActivityDefinition[]> {
  if (!auth.currentUser) throw new Error('Not authenticated');
  const ref = collection(db, 'users', userId, 'activityDefinitions');
  const snap = await getDocs(query(ref, orderBy('order', 'asc')));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }) as ActivityDefinition);
}

export async function addActivityDefinition(
  userId: string,
  def: Omit<ActivityDefinition, 'id' | 'createdAt' | 'updatedAt'>
): Promise<ActivityDefinition> {
  if (!auth.currentUser) throw new Error('Not authenticated');
  const ref = collection(db, 'users', userId, 'activityDefinitions');
  const now = Timestamp.now();
  const docRef = await addDoc(ref, { ...def, createdAt: now, updatedAt: now });
  return { id: docRef.id, ...def, createdAt: now, updatedAt: now };
}

export async function updateActivityDefinition(
  userId: string,
  defId: string,
  updates: Partial<Omit<ActivityDefinition, 'id' | 'createdAt'>>
): Promise<void> {
  if (!auth.currentUser) throw new Error('Not authenticated');
  const ref = doc(db, 'users', userId, 'activityDefinitions', defId);
  await updateDoc(ref, { ...updates, updatedAt: Timestamp.now() });
}

export async function deleteActivityDefinition(userId: string, defId: string): Promise<void> {
  if (!auth.currentUser) throw new Error('Not authenticated');
  await deleteDoc(doc(db, 'users', userId, 'activityDefinitions', defId));
}

export async function seedDefaultActivities(userId: string): Promise<ActivityDefinition[]> {
  if (!auth.currentUser) throw new Error('Not authenticated');
  const batch = writeBatch(db);
  const ref = collection(db, 'users', userId, 'activityDefinitions');
  const now = Timestamp.now();
  const defs: ActivityDefinition[] = [];

  SUGGESTED_ACTIVITIES.forEach((template, index) => {
    const docRef = doc(ref);
    const def: ActivityDefinition = {
      id: docRef.id,
      ...template,
      isActive: true,
      order: index,
      createdAt: now,
      updatedAt: now,
    };
    batch.set(docRef, def);
    defs.push(def);
  });

  await batch.commit();
  return defs;
}

// ─── Activity Logs ────────────────────────────────────────────────────────────

export async function getActivityLog(userId: string, weekStart: string): Promise<ActivityLog | null> {
  if (!auth.currentUser) throw new Error('Not authenticated');
  const ref = doc(db, 'users', userId, 'activityLogs', weekStart);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return { weekStart, ...snap.data() } as ActivityLog;
}

export async function upsertActivityLog(
  userId: string,
  log: Partial<ActivityLog> & { weekStart: string }
): Promise<void> {
  if (!auth.currentUser) throw new Error('Not authenticated');
  const ref = doc(db, 'users', userId, 'activityLogs', log.weekStart);
  await setDoc(ref, { ...log, updatedAt: Timestamp.now() }, { merge: true });
}

export async function toggleActivityCompletion(
  userId: string,
  weekStart: string,
  activityId: string,
  dateStr: string,
  completed: boolean
): Promise<void> {
  if (!auth.currentUser) throw new Error('Not authenticated');
  const ref = doc(db, 'users', userId, 'activityLogs', weekStart);
  const snap = await getDoc(ref);
  const existing = snap.exists() ? (snap.data() as ActivityLog) : null;
  const currentDates: string[] = existing?.completions?.[activityId] ?? [];

  const newDates = completed
    ? [...new Set([...currentDates, dateStr])]
    : currentDates.filter(d => d !== dateStr);

  await setDoc(
    ref,
    {
      weekStart,
      completions: { ...(existing?.completions ?? {}), [activityId]: newDates },
      updatedAt: Timestamp.now(),
    },
    { merge: true }
  );
}

export async function setSponsorSharing(
  userId: string,
  weekStart: string,
  shared: boolean
): Promise<void> {
  if (!auth.currentUser) throw new Error('Not authenticated');
  const ref = doc(db, 'users', userId, 'activityLogs', weekStart);
  await setDoc(
    ref,
    { weekStart, sharedWithSponsor: shared, updatedAt: Timestamp.now() },
    { merge: true }
  );
}

export async function saveSponsorComment(
  userId: string,
  weekStart: string,
  comment: string
): Promise<void> {
  if (!auth.currentUser) throw new Error('Not authenticated');
  const ref = doc(db, 'users', userId, 'activityLogs', weekStart);
  await setDoc(
    ref,
    {
      weekStart,
      sponsorComment: comment,
      sponsorCommentAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    },
    { merge: true }
  );
}

// ─── Auto-tracked completions ─────────────────────────────────────────────────

export async function getCheckinCompletionsForWeek(
  userId: string,
  weekDates: string[]
): Promise<{ morning: string[]; nightly: string[] }> {
  if (!auth.currentUser) throw new Error('Not authenticated');

  const result = { morning: [] as string[], nightly: [] as string[] };
  if (weekDates.length === 0) return result;

  const promises = weekDates.map(async (dateStr) => {
    const ref = doc(db, 'users', userId, 'checkins', dateStr);
    const snap = await getDoc(ref);
    if (!snap.exists()) return;
    const data = snap.data();
    if (data?.morning?.completed) result.morning.push(dateStr);
    if (data?.nightly?.completed) result.nightly.push(dateStr);
  });

  await Promise.all(promises);
  return result;
}

// ─── Sponsor view ─────────────────────────────────────────────────────────────

export async function getSharedActivityLogs(protegeId: string): Promise<ActivityLog[]> {
  if (!auth.currentUser) throw new Error('Not authenticated');
  const ref = collection(db, 'users', protegeId, 'activityLogs');
  const snap = await getDocs(query(ref, where('sharedWithSponsor', '==', true)));
  return snap.docs
    .map(d => ({ weekStart: d.id, ...d.data() }) as ActivityLog)
    .sort((a, b) => b.weekStart.localeCompare(a.weekStart));
}
